/* eslint @next/next/no-img-element: 0 */  // --> OFF

import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import { first, last, max, sort, sortBy, uniq } from '@/lib/array'
import rpc, { rpcTask } from '@/src/rpc'
import withLoading from '@/src/withLoading'
import FolderIcon from '@/components/icons/FolderIcon'
import useDeviceInfo from '@/src/useDeviceInfo'
import ArrowUturnLeftIcon from '@/components/icons/ArrowUturnLeftIcon'
import ArrowUturnRightIcon from '@/components/icons/ArrowUturnRightIcon'
import VideoCameraIcon from '@/components/icons/VideoCameraIcon'
import PopupMenu from '@/components/PopupMenu'
import Modal from '@/components/Modal'
import CloseIcon from '@/components/icons/CloseIcon'
import CubeIcon from '@/components/icons/CubeIcon'
import SwatchIcon from '@/components/icons/SwatchIcon'
import PhotoIcon from '@/components/icons/PhotoIcon'
import Popup from '@/components/Popup'
import PlusIcon from '@/components/icons/PlusIcon'
import TrashIcon from '@/components/icons/TrashIcon'
import FileUploader from '@/components/FileUploader'
import { sendMessage } from '@/src/useMessage'
import { ShadowFineTuning } from './ShadowPicker'
import { FontUI } from './FontPicker'
import { useAppRouter } from '@/src/useAppRouter'
import Square3Stack3dIcon from '../icons/Square3Stack3dIcon'
import Toggle from '../Toggle'
import Tooltip from '../Tooltip'
import ColorPicker from './ColorPicker'
import useStateWithHistory from './useImageEditHistory'
import CustomAlignmentIcon from '../icons/CustomAlignmentIcon'
import ArrowSmallLeftIcon from '../icons/ArrowSmallLeftIcon'
import { titleize } from '@/lib/string'
import StarIcon from '../icons/StarIcon'
import TextboxLayer from './TextboxLayer'
import ShapeLayer from './ShapeLayer'
import LogoLayer from './LogoLayer'
import VideoLayer from './VideoLayer'
import ImageLayer from './ImageLayer'
import CopyToClipboard from '../CopyToClipboard'
import CopyIcon from '../icons/CopyIcon'
import MoveIcon from '../icons/MoveIcon'
import HamburgerIcon from '../icons/legacy/HamburgerIcon'
import TextIcon from '../icons/TextIcon'
import SquareShapeIcon from '../icons/SquareShapeIcon'
import CircleShapeIcon from '../icons/CircleShapeIcon'
import EditIcon from '../icons/EditIcon'
import CogSixToothIcon from '../icons/CogSixToothIcon'
import EllipsisHorizontalIcon from '../icons/EllipsisHorizontalIcon'
import SearchIcon from '../icons/legacy/SearchIcon'


const defaultShadow = {
  blur:    6,
  color:   '#000000',
  opacity: 50,
  x:       0,
  y:       4,
}


const snappingDistance = 10 // pixels


export default function ImageEditorDesktop ({image, layers: initialLayers, brandColors, fonts, onCancel, onSave, mode, org}) {

  const router = useAppRouter()

  const {mac} = useDeviceInfo()

  const isTemplate = mode === 'template'
  const isCatalog  = mode === 'catalog'
  const simpleMode = !isTemplate && !isCatalog


  const [hasEdits,         setHasEdits        ] = useState(false)
  const [dragging,         setDragging        ] = useState(false)
  const [canvasBrightness, setCanvasBrightness] = useState(0.5)
  const [selectionFocus,   setSelecionFocus   ] = useState(0)
  const [previewBranding,  setPreviewBranding ] = useState(isCatalog)
  const [shortcutsVisible, setShortcutsVisible] = useState(false)
  const [editingText,      setEditingText     ] = useState(null)
  const [layersVisible,    setLayersVisible   ] = useState(isCatalog)

  const {state, setState, history} = useStateWithHistory({layers: initialLayers, activeLayerIds: []})
  const {layers, activeLayerIds} = state

  function setLayers (newValue) {
    // console.log('🔥 setLayers')
    setState(currentState => {
      if (typeof newValue === 'function') {
        newValue = newValue(currentState.layers)
      }
      return {
        ...currentState,
        layers: newValue
      }
    })
  }

  function setActiveLayerIds (newValue) {
    // console.log('🔥 setActiveLayerIds')
    setState(currentState => {
      if (typeof newValue === 'function') {
        newValue = newValue(currentState.activeLayerIds)
      }

      return {
        ...currentState,
        activeLayerIds: newValue
      }
    })
    history.setSquashKey('')
  }

  useEffect(() => {
    // If the layers have changed, update the hasEdits state
    if (JSON.stringify(state.layers) !== JSON.stringify(initialLayers)) {
      setHasEdits(true)
    } else {
      setHasEdits(false)
    }
  }, [state])

  const selectedLayers = layers.filter(l => activeLayerIds.includes(l.id))

  const firstTextbox = sortBy(layers, '-z').find(l => l.type === 'textbox')

  const openSansFont = fonts.find(f => f.name === 'OpenSans-Bold')
  const [defaultFont,      setDefaultFont     ] = useState( firstTextbox?.metadata?.font || openSansFont?.uid || '' )
  const [defaultFontSize,  setDefaultFontSize ] = useState( firstTextbox?.metadata?.size || 62 )
  const [defaultFontColor, setDefaultFontColor] = useState( firstTextbox?.color || '#FFFFFF' )
  const [defaultColor,     setDefaultColor    ] = useState( '#FFFFFF' )

  const emptyTextBoxes = layers.filter(l => l.type === 'textbox' && !(l.metadata.text || '').trim())
  const canSave = hasEdits && emptyTextBoxes.length === 0

  const {safari} = useDeviceInfo()

  const clickData  = useRef({})
  const dragData   = useRef({})
  const resizeData = useRef({})

  const ctrlKeyName = mac ? '⌘' : 'Ctrl'

  const logoSelected = !!layers.find(l => activeLayerIds.includes(l.id) && l.type === 'logo')
  const textboxesSelected = !!layers.find(l => activeLayerIds.includes(l.id) && l.type === 'textbox')
  const shapeSelected = !!layers.find(l => activeLayerIds.includes(l.id) && l.type === 'shape')
  const noSelection = activeLayerIds.length === 0
  const inactiveLayerOpacity = noSelection ? 1 : 1 - selectionFocus

  const maintainAspectRatio = !!layers.find(l => {
    return activeLayerIds.includes(l.id) && (
      l.type === 'photo' ||
      (l.type === 'shape' && l.metadata.shape === 'circle')
    )
  })

  const showVerticalAlignment = true

  const selectedLayersShadow = selectedLayers.map(l => l.metadata?.shadow)[0] || {...defaultShadow, opacity: 0}

  const selectedLayersOpacity  = selectedLayers.map(l => l.metadata?.opacity).filter(Boolean)[0] || 1
  const selectedLayersRounding = selectedLayers.map(l => l.metadata?.rounding).filter(Boolean)[0] || 0

  let selectedLayersAlignment = selectedLayers.map(l => {
    return {
      x: (l.metadata || {}).alignX,
      y: (l.metadata || {}).alignY,
    }
  })
  selectedLayersAlignment = uniq(selectedLayersAlignment)
  if (selectedLayersAlignment.length === 1) {
    selectedLayersAlignment = selectedLayersAlignment[0]
  } else {
    selectedLayersAlignment = {}
  }

  let selectedLayersFontId = selectedLayers.map(l => (l.metadata || {}).font)
  selectedLayersFontId = uniq(selectedLayersFontId)
  if (selectedLayersFontId.length === 1) {
    selectedLayersFontId = selectedLayersFontId[0]
  } else {
    selectedLayersFontId = ''
  }

  const selectedLayersFont = fonts.find(f => f.uid === selectedLayersFontId)


  let selectedLayersFontSize = selectedLayers.map(l => (l.metadata || {}).size)
  selectedLayersFontSize = uniq(selectedLayersFontSize)
  if (selectedLayersFontSize.length === 1) {
    selectedLayersFontSize = selectedLayersFontSize[0]
  } else {
    selectedLayersFontSize = ''
  }

  let selectedLayersKerning = selectedLayers.map(l => (l.metadata || {}).kerning)
  selectedLayersKerning = uniq(selectedLayersKerning)
  if (selectedLayersKerning.length === 1) {
    selectedLayersKerning = selectedLayersKerning[0]
  } else {
    selectedLayersKerning = ''
  }

  let selectedLayersLineSpacing = selectedLayers.map(l => (l.metadata || {}).spacing)
  selectedLayersLineSpacing = uniq(selectedLayersLineSpacing)
  if (selectedLayersLineSpacing.length === 1) {
    selectedLayersLineSpacing = selectedLayersLineSpacing[0]
  } else {
    selectedLayersLineSpacing = ''
  }

  const {width: viewportWidth} = useDeviceInfo()

  const imageWidth = image.width
  const maxEditorWidth = viewportWidth ? viewportWidth - 50 : 1024

  const previewWidth  = Math.min(imageWidth / 2, maxEditorWidth / 2)
  const layerWidth    = previewWidth / 4

  const imageScale = imageWidth / previewWidth
  const layerScale = image.width / layerWidth

  const sortedLayers   = sortBy(layers, 'z')
  const reversedLayers = layers.slice(0).reverse()
  const hasLogoLayer   = layers.find(l => l.type === 'logo')

  const hasVideoLayer      = !!layers.find(l => l.type === 'video')
  const canUploadFile      = !hasVideoLayer
  const canAddLogoLayer    = !hasLogoLayer && !hasVideoLayer
  const canAddImageLayer   = !hasVideoLayer
  const canAddTextBoxLayer = !hasVideoLayer
  const canEditShadowColor = simpleMode
  const canChangeBrandable = isTemplate || isCatalog
  const canChangeOpacity   = selectedLayers.length > 0
  const canChangeRounding  = selectedLayers.length > 0 && selectedLayers.every(l => l.type === 'shape' && l.metadata.shape === 'rectangle')

  const layerDragData = useRef({})
  const [layerDragZ, setLayerDragZ] = useState(-1)

  const snapToCenter = useRef(false)
  const ctrlKeyDown  = useRef(false)
  const altKeyDown   = useRef(false)
  const shiftKeyDown = useRef(false)

  const [selectionBoxSuppressed, setSelectionBoxSuppressed] = useState(false)

  function hideSelectionBox() {
    setSelectionBoxSuppressed(true)
  }

  function showSelectionBox() {
    setSelectionBoxSuppressed(false)
  }

  useEffect(() => {
    const sizesAndLocations = []
    activeLayerIds.forEach(id => {
      const layer = layers.find(l => l.id === id)
      if (!layer) return

      const {x, y, width, height} = layer
      sizesAndLocations.push( JSON.stringify({x, y, width, height}) )
    })

    // Only allow snapping if all the selected layers have the same size/position
    snapToCenter.current = uniq(sizesAndLocations).length === 1
  }, [layers, activeLayerIds])


  useEffect(() => {
    hideAdvancedTools()
  }, [activeLayerIds])


  const [canBeTemplate, setCanBeTemplate] = useState(false)
  useEffect(() => {
    if (!image.id) return
    checkIfTemplateAllowed()

    async function checkIfTemplateAllowed () {
      const allowed = await rpc('imageTemplates.canCreate', {imageId: image.id})
      setCanBeTemplate(allowed)
    }
  }, [image.id])

  const uncoloredLogoSelected = !!selectedLayers.find(l => l.type === 'logo' && !l.color)
  const canDelete          = activeLayerIds.length > 0
  const canChangeShadow    = activeLayerIds.length > 0 && !uncoloredLogoSelected
  const canChangeAlignment = logoSelected || textboxesSelected
  const canChangeColor     = logoSelected || textboxesSelected || shapeSelected


  function selectLayer(layer) {
    setActiveLayerIds([layer.id])
  }


  function toggleLayerSelection(layer) {
    if (activeLayerIds.includes(layer.id)) {
      setActiveLayerIds(activeLayerIds.filter(id => id !== layer.id))
    } else {
      setActiveLayerIds([...activeLayerIds, layer.id])
    }
  }


  function deselectAllOnDirectClick (e) {
    if (e.target === e.currentTarget) {
      deselectAllLayers()
    }
  }


  function deselectAllLayers () {
    hideAdvancedTools()
    setActiveLayerIds([])
    stopEditingText()
  }

  function stopEditingText () {
    if (editingText) {
      // Remove the focus from the active textarea
      const textArea = document.querySelector(`textarea:focus`)
      if (textArea) textArea.blur()

      // Fix issue with scrolling caused by text area that extends past edge
      const imagePreview = document.getElementsByClassName('image-preview')[0]
      if (imagePreview) {
        imagePreview.scrollLeft = 0
        imagePreview.scrollTop  = 0
      }

      setEditingText(null)
    }
  }

  function removeSelectedLayers () {
    const layersToDelete = selectedLayers.filter(l => l.type !== 'video')
    const deadLayerIds = layersToDelete.map(l => l.id)
    removeLayersByid(deadLayerIds)
  }

  function removeLayersByid(ids) {
    const remainingLayers = layers.filter(l => !ids.includes(l.id))
    deselectAllLayers()
    setLayers(remainingLayers)
  }


  useEffect(() => {
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('keyup', onKeyUp)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('keyup', onKeyUp)
    }

    function onKeyDown (event) {
      ctrlKeyDown.current  = !!event.metaKey || !!event.ctrlKey
      altKeyDown.current   = !!event.altKey
      shiftKeyDown.current = !!event.shiftKey

      if (editingText) {
        if (event.key === 'Escape' || (event.key === 'Enter' && ctrlKeyDown.current)) {
          deselectAllLayers()
        }
        return
      }

      // Arrow keys move selected layers
      if (activeLayerIds.length > 0 && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
        event.preventDefault()
        const distance = shiftKeyDown.current ? 10 : 1
        let deltaX = 0
        let deltaY = 0

        if (event.key === 'ArrowUp')    deltaY = -distance
        if (event.key === 'ArrowDown')  deltaY = distance
        if (event.key === 'ArrowLeft')  deltaX = -distance
        if (event.key === 'ArrowRight') deltaX = distance

        // Group arrow key movements into a single undo action
        history.setSquashKey('arrow-move:' + activeLayerIds.join(','))

        setLayers(currentLayers => {
          return currentLayers.map(layer => {
            if (!activeLayerIds.includes(layer.id)) return layer
            return {
              ...layer,
              x: layer.x + deltaX,
              y: layer.y + deltaY,
            }
          })
        })
        return
      }

      if (event.key === 'Backspace') {
        removeSelectedLayers()
        return
      }

      if (ctrlKeyDown.current && event.key === 'c') copySelectedLayers()
      if (ctrlKeyDown.current && event.key === 'v') pasteLayers()
      if (ctrlKeyDown.current && event.key === 'd') duplicateSelectedLayers()

      if (!event.metaKey && !event.ctrlKey) return

      if (event.key === 'z') {
        if (event.shiftKey) {
          history.redo()
        } else {
          history.undo()
        }
        event.preventDefault()
        return
      }
    }

    function onKeyUp (event) {
      ctrlKeyDown.current  = !!event.metaKey || !!event.ctrlKey
      altKeyDown.current   = !!event.altKey
      shiftKeyDown.current = !!event.shiftKey
    }

  }, [editingText, history, activeLayerIds])




  // Keyboard events for grouping, ungrouping and clearing selected layers
  useEffect(() => {
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }

    function onKeyDown (event) {
      if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key === 'g') {
        ungroupSelectedLayers()
        event.preventDefault()
        return
      } else if ((event.metaKey || event.ctrlKey) && event.key === 'g') {
        groupSelectedLayers()
        event.preventDefault()
        return
      } else if (((event.metaKey || event.ctrlKey) && event.key === 'd') || event.key === 'Escape') {
        deselectAllLayers()
        event.preventDefault()
      }
    }

    function groupSelectedLayers () {
      if (activeLayerIds.length <= 1) {
        alert('Select at least 2 layers to make a group')
        return
      }

      const selectedLayers = []
      const otherLayers = []

      layers.forEach(layer => {
        if (activeLayerIds.includes(layer.id)) {
          if (isGroup(layer)) {
            const subLayers = ungroupLayer(layer)
            if (!subLayers) {
              alert(`Failed to merge groups. Please ungroup first.`)
              return
            }
            selectedLayers.push(...subLayers)
          } else {
            selectedLayers.push(layer)
          }
        } else {
          otherLayers.push(layer)
        }
      })

      const newGroupLayer = groupLayers(selectedLayers)
      if (!newGroupLayer) return // In case of an alert was shown

      deselectAllLayers()
      setLayers(zValueCleanup([...otherLayers, newGroupLayer]))
      setActiveLayerIds([newGroupLayer.id])
    }


    function groupLayers(selectedLayers) {
      if (selectedLayers.find(l => l.type === 'logo')) {
        alert(`The logo layer can't be put into a group`)
        return
      }

      if (selectedLayers.find(l => l.type === 'textbox')) {
        alert(`A textbox layer can't be grouped`)
        return
      }

      if (selectedLayers.find(l => isGroup(l))) {
        alert(`You can't triple stamp a double-stamp! (No grouping groups)`)
        return
      }

      let left   = Number.POSITIVE_INFINITY
      let top    = Number.POSITIVE_INFINITY
      let right  = Number.NEGATIVE_INFINITY
      let bottom = Number.NEGATIVE_INFINITY
      let z      = Number.NEGATIVE_INFINITY

      selectedLayers.forEach((layer) => {
        left   = Math.min( left,   layer.x )
        top    = Math.min( top,    layer.y )
        right  = Math.max( right,  layer.x + layer.width )
        bottom = Math.max( bottom, layer.y + layer.height )

        // The new grouped layer will have the highest z value of all sublayers
        z = Math.max( z, layer.z )
      })

      const subLayers = selectedLayers.map(l => {
        return {
          ...l,
          x: l.x - left,
          y: l.y - top,
        }
      })

      const newLayer = {
        id:        newLayerId(),
        type:      '',
        width:     right - left,
        height:    bottom - top,
        x:         left,
        y:         top,
        z:         z,
        brandable: !!subLayers.find(l => l.brandable),
        layers:    zValueCleanup(subLayers),
      }

      return newLayer
    }


    function ungroupSelectedLayers () {
      if (activeLayerIds.length !== 1) {
        alert('Select 1 layer that you want to ungroup')
        return
      }

      const groupedLayer = layers.find(l => l.id === activeLayerIds[0])

      const ungroupedLayers = ungroupLayer(groupedLayer)
      if (!ungroupedLayers) return // In case of an alert was shown

      // Remove the grouped layer
      let allLayers = layers.filter(l => l.id !== groupedLayer.id)

      allLayers = zValueCleanup([...allLayers, ...ungroupedLayers])

      deselectAllLayers()
      setLayers(allLayers)
      setActiveLayerIds(ungroupedLayers.map(l => l.id))
    }


    function ungroupLayer(groupLayer) {
      if (groupLayer && !isGroup(groupLayer)) {
        alert(`You can't ungroup a non-group layer`)
        return
      }

      const ungroupedLayers = sortBy(groupLayer.layers, 'z').map((l, i) => {
        return {
          ...l,
          x: l.x + groupLayer.x,
          y: l.y + groupLayer.y,

          // Keep the z values in the same order as they were when grouped, but
          // but use a small decimal value to keep them together in the z-stack
          // when they are merged back into the root layer list, and put them
          // in the z-order of the group that they were just ungrouped from.
          z: groupLayer.z + (0.001 * i)
        }
      })

      return ungroupedLayers
    }

  }, [layers, activeLayerIds])




  useEffect(() => {
    if (dragging) {
      setupDragging()
    } else {
      teardownDragging()
    }
    return teardownDragging

    function dragMove (e) {
      let diffX = (e.clientX - dragData.current.xMouse) * imageScale
      let diffY = (e.clientY - dragData.current.yMouse) * imageScale

      // Don't let any of the layers move too far off secreen
      diffX = Math.max( dragData.current.minDiffX, diffX )
      diffY = Math.max( dragData.current.minDiffY, diffY )
      diffX = Math.min( dragData.current.maxDiffX, diffX )
      diffY = Math.min( dragData.current.maxDiffY, diffY )


      setLayers(
        layers.map(layer => {
          if (!activeLayerIds.includes(layer.id)) return layer

          const {x, y} = dragData.current.layerStart[layer.id]

          let newX = x + diffX
          let newY = y + diffY

          if (snapToCenter.current && !ctrlKeyDown.current) {
            const middleY = image.height / 2 - layer.height / 2
            const centerX = image.width  / 2 - layer.width  / 2

            if (Math.abs(newX - centerX) < snappingDistance) newX = centerX
            if (Math.abs(newY - middleY) < snappingDistance) newY = middleY

            if (Math.abs(newX) < snappingDistance) newX = 0
            if (Math.abs(newY) < snappingDistance) newY = 0

            if (Math.abs(newX + layer.width  - image.width ) < snappingDistance) newX = image.width  - layer.width
            if (Math.abs(newY + layer.height - image.height) < snappingDistance) newY = image.height - layer.height
          }

          return {
            ...layer,
            x: newX,
            y: newY,
          }
        })
      )
    }

    function stopDrag(e) {
      setDragging(false)
    }


    function setupDragging(e) {
      document.addEventListener('pointermove', dragMove)
      document.addEventListener('pointerup', stopDrag)

      // Only create one undo snapshot when moving the same layer multiple times
      history.setSquashKey('move:' + activeLayerIds.join(','))

      const padding = imageScale * 18 // pixels

      let minDiffX = Number.NEGATIVE_INFINITY
      let minDiffY = Number.NEGATIVE_INFINITY
      let maxDiffX = Number.POSITIVE_INFINITY
      let maxDiffY = Number.POSITIVE_INFINITY

      activeLayerIds.forEach(id => {
        const layer = layers.find(l => l.id === id)
        if (!layer) return

        let minX = 0
        let minY = 0
        let maxX = 0
        let maxY = 0

        if (layer.type === 'logo') {
          minX = padding
          minY = padding
          maxX = image.width  - layer.width  - padding
          maxY = image.height - layer.height - padding
        } else {
          minX = -image.width  + padding
          minY = -image.height + padding
          maxX =  image.width  - padding
          maxY =  image.height - padding
        }

        minDiffX = Math.max( minDiffX, minX - layer.x )
        minDiffY = Math.max( minDiffY, minY - layer.y )
        maxDiffX = Math.min( maxDiffX, maxX - layer.x )
        maxDiffY = Math.min( maxDiffY, maxY - layer.y )
      })

      dragData.current.minDiffX = minDiffX
      dragData.current.minDiffY = minDiffY
      dragData.current.maxDiffX = maxDiffX
      dragData.current.maxDiffY = maxDiffY
    }

    function teardownDragging(e) {
      document.removeEventListener('pointermove', dragMove)
      document.removeEventListener('pointerup', stopDrag)
    }
  }, [dragging, setDragging])


  function changeSelected(data, opts) {
    if (activeLayerIds.length === 0) return

    changeLayers(activeLayerIds, data, opts)
  }



  function changeLayers(ids, data, opts) {
    // The history squash key is used to group multiple changes into a single
    // history entry. This is useful for things like moving layers, where you
    // don't want to create a new history entry for every pixel moved.
    let squashKey = opts?.historySquashKey
    squashKey ||= Object.keys(data).sort().join(',') + ':' + [...ids].sort().join(',')
    history.setSquashKey(squashKey)

    // These are the keys that are at the root level of the layer object
    const rootLevelKeys = 'brandable color height type width x y z'.split(' ')

    // Clone the data object so we don't modify the original
    data = {...data}

    // Move the metadata keys out of the data object
    let metadata = {}
    Object.keys(data).forEach(key => {
      if (!rootLevelKeys.includes(key)) {
        metadata[key] = data[key]
        delete data[key]
      }
    })

    setLayers(currentLayers => {
      return currentLayers.map(layer => {
        if (!ids.includes(layer.id)) return layer

        // Keep track of the font settings for defaults on the next new textbox
        if (layer.type === 'textbox') {
          if (data.color)    setDefaultFontColor(data.color)
          if (metadata.font) setDefaultFont(metadata.font)
          if (metadata.size) setDefaultFontSize(metadata.size)
        }

        if (data.color) {
          setDefaultColor(data.color)
        }

        // Need to check this before merging the metadata
        const logoColorRemoved = layer.type === 'logo' && data.color === ''

        // Merge the new metadata with the existing metadata for the layer
        const newMetadata = {...layer.metadata, ...metadata}

        // If the logo color was removed, make sure the shadow is also removed
        if (logoColorRemoved) metadata.shadow = null

        const layerData = {
          ...layer,
          ...data,
          metadata: {
            ...newMetadata,
          },
        }

        return layerData
      })
    })
  }

  function changeLayerText(layer, text) {
    changeLayers([layer.id], {text: text})
  }


  function handleToggleShadow () {
    const hasShadow = selectedLayersShadow && Object.keys(selectedLayersShadow).length > 0
    if (hasShadow) {
      changeSelected({shadow: null})
    } else {
      changeSelected({shadow: defaultShadow})
    }
  }


  const [advancedToolName,    setAdvancedToolName   ] = useState(null)
  const [advancedToolVisible, setAdvancedToolVisible] = useState(false)

  function showAdvancedTools(popupName) {
    setAdvancedToolName(popupName)
    setAdvancedToolVisible(true)
  }

  function hideAdvancedTools() {
    setAdvancedToolVisible(false)
  }


  const [showUpload, setShowUpload] = useState(false)
  const [showGalleryPicker, setShowGalleryPicker] = useState(false)
  const [galleryAssets, setGalleryAssets] = useState([])
  const [galleryQuery, setGalleryQuery] = useState('')
  const gallerySearchTimer = useRef(null)
  const hasGallery = org?.config?.gallery

  function addImageLayer() {
    if (hasGallery) {
      setShowGalleryPicker(true)
      loadGalleryAssets()
    } else {
      setShowUpload(true)
    }
  }

  async function loadGalleryAssets () {
    try {
      const result = await rpc('gallery.all', {})
      setGalleryAssets(result)
    } catch (err) {
      console.log('Failed to load gallery assets', err)
    }
  }

  function handleGallerySearchInput (value) {
    setGalleryQuery(value)
    clearTimeout(gallerySearchTimer.current)
    gallerySearchTimer.current = setTimeout(async () => {
      try {
        if (!value.trim()) {
          loadGalleryAssets()
        } else {
          const result = await rpc('gallery.search', {query: value})
          setGalleryAssets(result)
        }
      } catch (err) {
        console.log('Gallery search failed', err)
      }
    }, 300)
  }

  function pickFromGallery (asset) {
    setShowGalleryPicker(false)
    const fileData = {
      id: asset.fileId,
      url: asset.url,
      type: asset.assetType === 'video' ? 'video/mp4' : 'image/jpeg',
      metadata: {
        dimensions: {
          width: asset.width,
          height: asset.height,
        }
      }
    }
    handleUpload([fileData])
  }

  function handleUpload (files) {
    setShowUpload(false)

    if (!canUploadFile) return

    // Put it above behind any text or logo layers
    const topLayers = layers.filter(l => l.type !== 'logo' && l.type !== 'textbox')
    const zValues = topLayers.map(l => l.z)

    const newLayers = files.map(file => {
      let z = (max(zValues) || 0) + 0.01
      zValues.push(z)

      let height = file.metadata.dimensions.height
      let width  = file.metadata.dimensions.width

      // Scale the image to fit the preview area
      const scale = Math.min(image.width / width, image.height / height)
      if (scale < 1) {
        width *= scale
        height *= scale
      }

      const type = file.type.startsWith('video') ? 'video' : 'photo'

      const layer = {
        id:     newLayerId(),
        type:   type,
        width:  width,
        height: height,
        x:      image.width  / 2 - width  / 2,
        y:      image.height / 2 - height / 2,
        z:      z,
        file:   file,
      }
      return layer
    })

    setLayers([...layers, ...newLayers])
    setActiveLayerIds(newLayers.map(l => l.id))
  }


  function addLogoLayer() {
    // Hard-coded sizes to match the logo area placeholder images (for now)
    const logoWidth  = 520
    const logoHeight = 150

    const logoLayer = {
      id:     newLayerId(),
      type:   'logo',
      width:  logoWidth,
      height: logoHeight,
      x:      image.width  / 2 - logoWidth  / 2,
      y:      image.height / 2 - logoHeight / 2,
      z:      max(layers.map(l => l.z) || 0) + 1, // Put it on top
      metadata: {
        alignY: 'middle',
        alignX: 'center',
      }
    }
    setLayers([...layers, logoLayer])
    selectLayer(logoLayer)
  }


  function addShapeLayer(shapeName) {
    // Put it above behind any text or logo layers
    const topLayers = layers.filter(l => l.type !== 'logo' && l.type !== 'textbox')
    const zValues = topLayers.map(l => l.z)

    let width    = 200
    let height   = 200
    let x        = image.width  / 2 - width  / 2
    let y        = image.height / 2 - height / 2
    let z        = (max(zValues) || 0) + 0.01
    let color    = defaultColor || '#FFFFFF'
    let rounding = 0

    // Rectangles get rounding (by default)
    let backdropMode = false
    if (shapeName === 'rectangle') {
      rounding = Math.round(image.width / 50)

      const layerToBackdrop = selectedLayers.length === 1 && selectedLayers.find(l => {
        return canHaveBackdrop(l) && !hasBackdrop(l)
      })

      if (layerToBackdrop) {
        backdropMode = true
        const verticalPadding = 40
        const horizontalPadding = 60
        width  = layerToBackdrop.width  + horizontalPadding
        height = layerToBackdrop.height + verticalPadding
        z      = layerToBackdrop.z - 0.01
        x      = layerToBackdrop.x - horizontalPadding / 2
        y      = layerToBackdrop.y - verticalPadding / 2

        color = [...brandColors, '#FFFFFF', '#000000'].find(c => c !== layerToBackdrop.color)

        if (layerToBackdrop.type === 'photo') {
          color = '#FFFFFF'
        }
      }
    }

    // Look for existing shapes with very similar size and position, and if one
    // exists, move this one so they don't overlap perfectly
    let overlapChecks = 10
    while (overlapChecks > 0) {
      overlapChecks -= 1

      const existingShape = layers.find(l => {
        return (
          l.type === 'shape' &&
          l.metadata.shape === shapeName &&
          Math.abs(l.x - x) < 10 &&
          Math.abs(l.y - y) < 10 &&
          Math.abs(l.width  - width)  < 10 &&
          Math.abs(l.height - height) < 10
        )
      })

      if (existingShape) {
        // Move the new shape to the right of the existing shape
        x = existingShape.x + image.width / 50
        y = existingShape.y + image.width / 50
      } else {
        break
      }
    }

    if (overlapChecks <= 0) {
      sendMessage('error', 'Too many shapes added')
      return
    }

    const shapeLayer = {
      id:      newLayerId(),
      type:    'shape',
      width:    width,
      height:   height,
      x:        x,
      y:        y,
      z:        z,
      color:    color,
      metadata: {
        shape:    shapeName,
        opacity:  1,
        rounding: rounding,
        shadow:   defaultShadow,
      }
    }
    setLayers([...layers, shapeLayer])
    selectLayer(shapeLayer)
  }


  function addTextBoxLayer() {
    let width  = Math.round(image.width * 0.9)
    let height = Math.round(image.height * 0.2)

    // Center the text
    let x = image.width  / 2 - width  / 2
    let y = image.height / 2 - height / 2
    let z = max(layers.map(l => l.z) || 0) + 1 // Put it on top

    let color    = defaultFontColor || '#FFFFFF'
    let fontSize = '' // Leave it empty to trigger auto-sizing
    let fontId   = defaultFont || fonts[0]?.uid

    let text   = 'Add your text here'
    let alignX = 'center'
    let alignY = 'middle'

    const layer = {
      id:     newLayerId(),
      type:   'textbox',
      width:  width,
      height: height,
      x:      x,
      y:      y,
      z:      z,
      color:  color,
      metadata: {
        alignY: alignY,
        alignX: alignX,
        font:   fontId,
        size:   fontSize,
        text:   text,
        spacing: 0,
        kerning: 0,
        shadow: defaultShadow,
      }
    }
    setLayers([...layers, layer])
    selectLayer(layer)
    startEditingTextLayer(layer)
  }

  function handlePointerDownOnLayer (e, layer) {
    e.stopPropagation()

    clickData.current = {
      layerId: layer.id,
      x:       e.clientX,
      y:       e.clientY,
      target:  e.target,
      layerWasSelected: isSelected(layer),
    }

    if (ctrlKeyDown.current || shiftKeyDown.current) {
      if (isSelectable(layer) || activeLayerIds.includes(layer.id)) {
        toggleLayerSelection(layer)
        return
      }
    }

    if (isSelectable(layer)) {
      selectLayer(layer)
    } else if (layer.type === 'textbox' && isSelected(layer) && activeLayerIds.length === 1) {
      startEditingTextLayer(layer)
      return
    } else if (isEditingText(layer)) {
      return
    } else if (!isSelected(layer)) {
      deselectAllLayers()
    }

    if (isDraggable(layer)) {
      startDragging(e, layer)
    }
  }


  function canHaveBackdrop(layer) {
    if (layer.type === 'shape') return false
    if (layer.type === 'video') return false

    if (layer.width >= image.width) return false
    if (layer.height >= image.height) return false

    return true
  }


  function hasBackdrop (layer) {
    layers.find(l => {
      if (l.type !== 'shape') return false
      if (l.metadata.shape !== 'rectangle') return false
      if (l.metadata.backdrop !== layer.id) return false

      if (l.x > layer.x) return false
      if (l.y > layer.y) return false
      if (l.x + l.width  < layer.x + layer.width)  return false
      if (l.y + l.height < layer.y + layer.height) return false
      if (l.z < layer.z) return false

      return true
    })
  }


  function handlePointerUpOnLayer (e, layer) {

    if (resizing) {
      return
    }

    e.stopPropagation()

    if (dragging) {
      setDragging(false)

      const {xMouse, yMouse} = dragData.current

      const diffX    = Math.abs(e.clientX - xMouse)
      const diffY    = Math.abs(e.clientY - yMouse)
      const distance = Math.sqrt(diffX * diffX + diffY * diffY)
      const dragged  = (distance >= 2)

      if (dragged) return
    }

    // Ignore the up event if the down event was on a different layer
    if (clickData.current.layerId !== layer.id) {
      return
    }

    // A second tap on a textbox will switch it into editing mode
    const shouldEditText = (
      isSelected(layer) &&
      layer.type === 'textbox' &&
      clickData.current.layerWasSelected
    )
    if (shouldEditText) {
      startEditingTextLayer(layer)
      return
    }

    if (isDraggable(layer)) {
      // setActiveLayerIds([layer.id])
    } else if (isEditingText(layer)) {
      // Don't lose focus when editing text
      e.preventDefault()
    }
  }


  function handleDoubleClickOnLayer (e, layer) {
    if (layer.type === 'textbox') {
      startEditingTextLayer(layer)
    } else {
      console.warn('Double click not implemented for', layer.type)
    }
  }

  function startEditingTextLayer (layer) {
    // You can't triple-stamp a double-stamp!
    if (editingText && editingText.id === layer.id) {
      // Don't try to start editing the same textbox (again).
      return
    }

    setEditingText(layer)

    // FIXME: Instead of using a timeout, we should set a an autoFocus property
    // on the TextboxLayer component, and let it handle the focus itself _after_
    // the font has been loaded and resized correctly.
    setTimeout(() => {
      const textLayerId = `text-layer-editor-${layer.id}`
      const textarea = document.getElementById(textLayerId)

      if (!textarea) {
        console.error(`Could not find textarea for layer ${layer.id} with id ${textLayerId}`)
        return
      }

      textarea.focus()
      textarea.setSelectionRange(0, textarea.value.length)
    }, 100)
  }

  function setLayerType(layer, type) {
    setLayers(
      layers.map(l => l.id === layer.id ? {...l, type: type} : l)
    )
  }

  function toggleLayerBrandable(layer) {
    setLayers(
      layers.map(l => {
        if (l.id !== layer.id) return l

        // Don't allow brandable flag on logos that don't have a color
        const colorlessLogo = (l.type === 'logo' && hasNoColor(l))
        if (colorlessLogo) return l

        return {
          ...l,
          brandable: !l.brandable,
        }
      })
    )
  }


  async function createTemplate() {
    withLoading(async () => {
      const template = await rpc('imageTemplates.copyCatalogImage', {imageId: image.id})
      setCanBeTemplate(false)
      if (template.catalogId) {
        router.push(`/backstage/image-templates?catalog=${template.catalogId}`)
      }
    })
  }


  function toggleShortcuts() {
    setShortcutsVisible(v => !v)
  }


  async function save() {
    deselectAllLayers()

    if (!firstTextbox && isTemplate) {
      if (!confirm(`You should add text to this image template, or there won't be anywhere for text to be inserted. Are you sure you want to save it?`)) {
        return
      }
    }

    withLoading(async () => {
      // Clean up the z values so they are consecutive integers (0, 1, 2, 3, ...)
      let layerData = zValueCleanup(layers)
      try {
        const task = await rpcTask('images.updateLayersInBackground', {imageId: image.id, layers: layerData, orgId: org?.id}, {throwErrors: true})
        const newImage = await task.result()
        if (onSave) onSave(newImage)
      } catch (e) {
        console.error('Error saving layers', e)
        sendMessage('error', "Couldn't save the image")
        return
      }
    })
  }

  function hasNoColor(layer) {
    // Check if color is missing or completely transparent
    return !layer.color || layer.color.match(/^#?.{6}00$/)
  }

  function isGroup(layer) {
    return layer.layers && layer.layers.length > 0
  }

  function isDraggable(layer) {
    if (layer.type === 'video') return false
    // if (editingText?.id === layer.id) return false

    if (isSelected(layer)) return true

    if (!isSelectable(layer)) return false

    return true
  }

  function isEditingText(layer) {
    return editingText?.id === layer.id && activeLayerIds.includes(layer.id)
  }


  // This is needed (for now) to deal with the way that we imported our layers
  // where they are all the full size of the image. To fix this we either need
  // to crop them, or we need a selection algorithm that can see past the
  // transparent areas of a layer to find the next layer.
  function isLocked(layer) {
    if (isSelected(layer)) return false

    if (layer.width === image.width && layer.height === image.height) {
      return true
    }
  }


  function isSelected(layer) {
    return activeLayerIds.includes(layer.id)
  }

  function isSelectable(layer) {
    if (resizing) return false

    if (isSelected(layer)) return false

    if (isLocked(layer)) return false

    const backgroundLayer = sortBy(layers, 'z')[0]
    if (backgroundLayer && backgroundLayer.id === layer.id) return false

    return true
  }


  function startDragging(e, layer) {
    if (!isDraggable(layer)) return

    setDragging(true)

    dragData.current = {
      xMouse:  e.clientX,
      yMouse:  e.clientY,
      layerStart: {},
    }

    layers.forEach(layer => {
      if (!activeLayerIds.includes) return
      const {x, y} = layer
      dragData.current.layerStart[layer.id] = {x, y}
    })
  }

  const [resizing, setResizing] = useState(null)

  function startResize(e, layer, handleName) {
    e.stopPropagation()
    e.preventDefault()

    // Only create one undo snapshot when resizing the same layer multiple times
    history.setSquashKey('resize:' + activeLayerIds.join(','))

    setResizing(handleName)
    resizeData.current = {
      xStart:  e.clientX,
      yStart:  e.clientY,
      layer:   layer,
      start:   {
        x: layer.x,
        y: layer.y,
        width: layer.width,
        height: layer.height,
      },
    }
  }

  useEffect(() => {
    if (!resizing) return

    function resizeMove(e) {

      let diffX = (e.clientX - resizeData.current.xStart) * imageScale
      let diffY = (e.clientY - resizeData.current.yStart) * imageScale

      setLayers(layers => {
        return layers.map(layer => {
          if (layer.id !== resizeData.current.layer.id) return layer

          // Get the starting size and position of the layer
          let {x, y, width, height} = resizeData.current.start

          const horizontalResize = resizing.includes('left') || resizing.includes('right')
          const verticalResize   = resizing.includes('top')  || resizing.includes('bottom')

          // Figure out how much the size should change
          let widthChange  = horizontalResize ? diffX * (altKeyDown.current ? 2 : 1) : 0
          let heightChange = verticalResize   ? diffY * (altKeyDown.current ? 2 : 1) : 0

          if (resizing.includes('top'))  heightChange *= -1
          if (resizing.includes('left')) widthChange  *= -1

          let newHeight = Math.round(height + heightChange)
          let newWidth  = Math.round(width  + widthChange)

          // Don't let the layer get too small
          let [minWidth, minHeight] = [40, 40]
          if (layer.type === 'logo') {
            [minWidth, minHeight] = [520, 150]
          }
          if (layer.type === 'textbox') {
            [minWidth, minHeight] = [120, 60]
          }

          if (newWidth  <  minWidth) newWidth  = minWidth
          if (newHeight < minHeight) newHeight = minHeight

          // Maintain the aspect ratio for things like photos (or holding Shift)
          const maintainRatio = maintainAspectRatio || shiftKeyDown.current
          if (maintainRatio && height) {
            const xScale = newWidth  / width
            const yScale = newHeight / height

            let scale = 1
            if (horizontalResize && verticalResize) scale = Math.min(xScale, yScale)
            else if (horizontalResize)              scale = xScale
            else if (verticalResize)                scale = yScale
            else                                    scale = 1

            const minScale = Math.max(minWidth / width, minHeight / height)
            if (scale < minScale) scale = minScale

            newWidth  = Math.round(width  * scale)
            newHeight = Math.round(height * scale)
          }

          // Figure out how to move the layer based on resize type and direction
          let xFactor = 0
          if (resizing.includes('left')) xFactor = -1
          if (horizontalResize && altKeyDown.current ) xFactor = -0.5
          if (maintainRatio && verticalResize && !horizontalResize) xFactor = -0.5
          x += xFactor * (newWidth - width)

          let yFactor = 0
          if (resizing.includes('top')) yFactor = -1
          if (verticalResize && altKeyDown.current) yFactor = -0.5
          if (maintainRatio && horizontalResize && !verticalResize) yFactor = -0.5
          y += yFactor * (newHeight - height)

          // Update the layer with the new size
          width  = newWidth
          height = newHeight

          return { ...layer, x, y, width, height }
        })
      })
    }

    function stopResize(e) {
      setResizing(null)

      document.removeEventListener('pointermove', resizeMove)
      document.removeEventListener('pointerup', stopResize)
    }

    document.addEventListener('pointermove', resizeMove)
    document.addEventListener('pointerup', stopResize)
  }, [resizing])


  function revert() {
    if (!onCancel) return

    if (history.canUndo && !confirm('If you close without saving, your changes will be lost.')) {
      return
    }

    onCancel()
  }


  function filterForLayer(layer, scale) {
    if (layer.metadata?.shadow) {
      return `drop-shadow(${shadowStyle(layer, scale)})`
    } else {
      return ''
    }
  }


  function shadowStyle(layer, scale) {
    if (!layer.metadata?.shadow) return ''

    let {blur, color, opacity, x, y} = layer.metadata.shadow
    x = x / scale
    y = y / scale
    blur = blur / scale

    const shadowColor = hexToRgba(color, opacity)
    return `${x}px ${y}px ${blur}px ${shadowColor}`
  }


  let showCenterLine = false
  let showMiddleLine = false

  activeLayerIds.forEach(id => {
    const layer = layers.find(l => l.id === id)
    if (!layer) return

    showCenterLine = showCenterLine || (
      layer && layer.x === (image.width / imageScale - layer.width / imageScale)
    )
    showMiddleLine = showMiddleLine || (
      layer && layer.y === (image.height / imageScale - layer.height / imageScale)
    )
  })

  if (!dragging || !snapToCenter.current) {
    showCenterLine = false
    showMiddleLine = false
  }

  const layerInDrag = layerDragData.current.layer


  function closeMenuAnd(fn) {
    return function () {
      setContentMenuPosition(null)
      fn()
    }
  }


  const [contextMenuType, setContextMenuType] = useState(null)

  const contextMenu = (
    <div className='flex-col'>
      <button className='popup-item flex-row gap-2' onClick={closeMenuAnd(addImageLayer)} disabled={!canAddImageLayer}>
        <PhotoIcon/>
        Add Image
      </button>
      <button className='popup-item flex-row gap-2' onClick={closeMenuAnd(addLogoLayer)} disabled={!canAddLogoLayer}>
        <CubeIcon/>
        Add Logo
      </button>
      <button className='popup-item flex-row gap-2' onClick={closeMenuAnd(addTextBoxLayer)} disabled={!canAddTextBoxLayer}>
        <TextIcon/>
        Add Text
      </button>
      <hr className='popup-divider'/>
      <button className='popup-item flex-row gap-2' onClick={closeMenuAnd(() => addShapeLayer('circle'))} disabled={!canAddImageLayer}>
        <CircleShapeIcon filled/>
        Add Circle
      </button>
      <button className='popup-item flex-row gap-2' onClick={closeMenuAnd(() => addShapeLayer('rectangle'))} disabled={!canAddImageLayer}>
        <SquareShapeIcon filled/>
        Add Rectangle
      </button>
    </div>
  )


  const layerContextMenu = (
    <div className='flex-col'>
      <button className='popup-item' onClick={closeMenuAnd(duplicateSelectedLayers)}>
        Duplicate <span className='opacity-30'>({ctrlKeyName}+D)</span>
      </button>
      <hr className='popup-divider'/>
      <button className='popup-item' onClick={closeMenuAnd(moveToFront)}>
        Move to Front
      </button>
      { canChangeRounding &&
        <>
          <hr className='popup-divider'/>
          <div className='flex-col flex-align-center full-width px-3 py-1'>
            <label>Corner Rounding</label>
            <input
              className='full-width'
              type="range"
              value={selectedLayersRounding}
              min="0"
              max="200"
              onChange={e => changeSelected({rounding: Number(e.target.value)})}
              disabled={!canChangeRounding}
              onPointerDown={hideSelectionBox}
              onPointerUp={showSelectionBox}
            />
          </div>
        </>
      }
    </div>
  )

  const [contentMenuPosition, setContentMenuPosition] = useState(null)


  function handleContextMenu (e) {
    e.preventDefault()
    setContextMenuType('image')
    setContentMenuPosition({x: e.clientX, y: e.clientY})
  }


  function handleLayerRightClick (e) {
    e.preventDefault()
    e.stopPropagation()
    setContextMenuType('layer')
    setContentMenuPosition({x: e.clientX, y: e.clientY})
  }


  async function copySelectedLayers () {
    const selectedLayers = layers.filter(l => activeLayerIds.includes(l.id))
    const copiedLayers = selectedLayers.map(layer => {
      return {...layer}
    })
    await copyToClipboard(JSON.stringify(copiedLayers))
  }


  async function duplicateSelectedLayers () {
    const selectedLayers = layers.filter(l => activeLayerIds.includes(l.id))
    const newLayers = selectedLayers.map(layer => {
      return {
        ...layer,
        id: newLayerId(),
        x: layer.x + 20,
        y: layer.y + 20,
        z: max(layers.map(l => l.z) || 0) + 1,
      }
    })

    setLayers([...layers, ...newLayers])
    setActiveLayerIds(newLayers.map(l => l.id))
  }


  function moveToFront () {
    setLayers( layers => {
      const maxZ = max(layers.map(l => l.z) || 0)
      return layers.map((layer, i) => {
        if (activeLayerIds.includes(layer.id)) {
          return {
            ...layer,
            z: maxZ + 0.001 * i,
          }
        }
        return layer
      })
    })
  }


  async function pasteLayers () {
    let copiedLayers = null
    try {
      copiedLayers = await navigator.clipboard.readText()
    } catch (e) {
      console.log('Error reading clipboard', e)
    }
    if (!copiedLayers) return

    let pastedLayers = null
    try {
      pastedLayers = JSON.parse(copiedLayers)
    } catch (e) {
      console.error('Error parsing copied layers', e)
      return
    }

    const maxZ = max(layers.map(l => l.z) || 0)

    const newLayers = pastedLayers.map(layer => {
      return {
        ...layer,
        id: newLayerId(),
        x: layer.x + 10,
        y: layer.y + 10,
        z: maxZ + layer.z / 1000,
      }
    })

    setLayers([...layers, ...newLayers])
    setActiveLayerIds(newLayers.map(l => l.id))
  }


  function handlePreviewClick (e) {
    if (resizing) {
      return
    }

    if (contentMenuPosition) {
      setContentMenuPosition(null)
      return
    }

    if (e.target === e.currentTarget) {
      deselectAllLayers()
    }
  }

  const [colorResetPosition, setColorResetPosition] = useState(null)


  function handleRemoveLogoColor () {
    setLayers(
      layers.map(l => {
        if (l.type !== 'logo') return l
        return {...l, color: '', brandable: false}
      })
    )
  }


  // Use an SVG filter to show a preview of what branding might look like
  function renderLayer (layer, readOnly) {
    const brandColor = brandColorForLayer(layer)
    const brandingFilterId = "branding-filter-preview-" + layer.id
    const brandingFilter = (layer.brandable) && <>
      <svg width="0" height="0">
        <filter id={brandingFilterId}>
          <feColorMatrix type="matrix" values="0.33 0.33 0.33 0 0
                                              0.33 0.33 0.33 0 0
                                              0.33 0.33 0.33 0 0
                                              0 0 0 1 0" result="gray" />

          <feFlood floodColor={brandColor} result="color" /> <feComposite operator="in" in="color" in2="SourceAlpha" result="color-masked" />

          <feBlend mode="hard-light" in="gray" in2="color-masked" />
        </filter>
      </svg>
    </>

    const filter = layer.brandable ? `url(#${brandingFilterId})` : ''

    return <>
      {brandingFilter}
      <div className="absolute-full" style={{filter}}>
        {renderLayerContents(layer, readOnly)}
      </div>
    </>
  }


  // We need to pick a "random" color to demonstrate how the layer will look
  // when branding, but we need that color to be relative to the original color
  // so that layers with similar colors will get a similar branding color,
  // because that is how the branding engine works -- layers with similar hues
  // will get grouped together and have the same brand color applied.
  function brandColorForLayer(layer) {
    const layerColor = layer.color || randomColorAtIndex(layer.id)
    return generateBrandingColor(layerColor)
  }


  function randomColorAtIndex(index) {
    // Deal with indexes on new layers (that are text and number combined)
    if (typeof index === 'string') {
      index = index.match(/\d+/)[0] || ''
      if (index) {
        index = parseInt(index)
      } else {
        index = Math.round(Math.random() * 100000)
      }
    }

    const colors = [
      '#ff8989',
      '#ffb589',
      '#ffe189',
      '#f0ff89',
      '#c4ff89',
      '#98ff89',
      '#89ffa7',
      '#89ffd3',
      '#89feff',
      '#89d3ff',
      '#89a7ff',
      '#9889ff',
      '#c489ff',
      '#f089ff',
      '#ff89e1',
      '#ff89b5',
    ]

    return colors[index % colors.length]
  }


  function renderLayerContents (layer, readOnly) {
    if (layer.type === 'logo') {
      return <LogoLayer layer={layer}/>
    }

    if (layer.type === 'shape') {
      return <ShapeLayer layer={layer}/>
    }

    if (layer.type === 'textbox') {
      return (
        <TextboxLayer
          layer={{...layer, font: fonts.find(f => f.uid === layer.metadata.font)}}
          editingText={isEditingText(layer)}
          changeLayers={changeLayers}
          onBlur={() => changeLayers([layer.id], {text: cleanupText(layer.metadata.text)})}
          readOnly={readOnly}
        />
      )
    }

    if (layer.type === 'video') {
      return <VideoLayer layer={layer} readOnly/>
    }

    if (isGroup(layer)) {
      return (
        <div
          className='absolute'
          style={{
            width:  layer.width,
            height: layer.height,
            filter: filterForLayer(layer, imageScale)
          }}
        >
          { sortBy(layer.layers, 'z').map( l => (
            <div
              key={l.id}
              className='absolute'
              style={{
                left:   l.x,
                top:    l.y,
                width:  l.width,
                height: l.height,
              }}
            >
              {renderLayer(l, readOnly)}
            </div>
          ))}
        </div>
      )
    }

    return <ImageLayer layer={layer}/>
  }


  return <>
    <FileUploader onUpload={handleUpload} multiple accept={canUploadFile ? 'image/*' : 'nada'} supressErrorMessage>

      <div onClick={e => {
        if (e.target === e.currentTarget) {
          deselectAllLayers()
        }
      }}
      id="image-editor"
      style={{
        height: image.height / imageScale,
        minHeight: 480,
      }}
      className={classNames("flex-row flex-stretch relative", {
        'drag-in-progress': dragging,
        'cursor-grabbing': dragging,
        'preview-branding': previewBranding
      })}>
        <div
          className='bg-ccc flex-row flex-center'
          style={{
            width:  image.width / imageScale,
            height: '100%',
          }}
        >
          <div
            className='flex-row bg-gray relative'
            style={{
              width:  image.width / imageScale,
              height: image.height / imageScale,
            }}
          >
            <div
              className="image-preview overflow-hidden"
              style={{
                width:  image.width / imageScale,
                height: image.height / imageScale,
              }}
              onContextMenu={handleContextMenu}
              onPointerUp={handlePreviewClick}
            >
              <div className="canvas-background absolute-full" onClick={deselectAllOnDirectClick}/>
              {showCenterLine && <div className='center-line'/>}
              {showMiddleLine && <div className='middle-line'/>}

              {sortedLayers.map(layer => {
                const isTextBox = layer.type === 'textbox'
                const hideSelection = (dragging || resizing) && !isTextBox
                const showSelectedOutline = isSelected(layer) && !hideSelection && !selectionBoxSuppressed

                return (
                <div
                  className={
                    classNames("layer", {
                      inactive:   !isSelected(layer),
                      active:     activeLayerIds.includes(layer.id),
                      draggable:  isDraggable(layer),
                      dragging:   dragging && activeLayerIds.includes(layer.id),
                      brandable:  layer.brandable,
                      selectable: isSelectable(layer),
                      locked:     isLocked(layer),
                      selected:   showSelectedOutline,
                    })
                  }
                  key={layer.id}
                  style={{
                    height: layer.height / imageScale,
                    width:  layer.width  / imageScale,
                    left:   layer.x      / imageScale,
                    top:    layer.y      / imageScale,
                  }}
                  onPointerDown={e => handlePointerDownOnLayer(e, layer)}
                  onPointerUp={e => handlePointerUpOnLayer(e, layer)}
                  onDoubleClick={e => handleDoubleClickOnLayer(e, layer)}
                  onContextMenu={e => isSelected(layer) ? handleLayerRightClick(e) : null}
                >
                  {(() => {
                    const handleDiameter = 14
                    const handleOffset = handleDiameter / 2 + 1

                    if (!isSelected(layer)) return null
                    if (activeLayerIds.length !== 1) return null

                    if (layer.type === 'video') return null

                    const canResize = !isGroup(layer) && !dragging && !selectionBoxSuppressed
                    const showLayerTools = !dragging && !resizing
                    const toolbarStyle = {}

                    const distanceFromEdge = 70 * imageScale

                    // Keep the toolbar within the preview area
                    if (layer.y > distanceFromEdge) {
                      // Option #1: Put it above the layer
                      toolbarStyle.top = -60
                    } else if (image.height - (layer.y + layer.height) > distanceFromEdge) {
                      // Option #2: Put it below the layer
                      toolbarStyle.top = layer.height / imageScale + 25
                    } else if (image.width - (layer.x + layer.width) > 100) {
                      // Option #3: Put it on the right side of the layer
                      toolbarStyle.left = layer.width / imageScale + 25
                      toolbarStyle.right = 'unset'
                      toolbarStyle.top = 0
                      toolbarStyle.bottom = 0
                      toolbarStyle.flexDirection = 'column'
                    } else if (layer.x > distanceFromEdge) {
                      // Option #4: Put it on the left side of the layer
                      toolbarStyle.right = 'unset'
                      toolbarStyle.left = -60
                      toolbarStyle.top = 0
                      toolbarStyle.bottom = 0
                      toolbarStyle.flexDirection = 'column'
                    } else {
                      // Option #5: Put it just below the top of the preview area
                      toolbarStyle.right = 0
                      toolbarStyle.left = 0
                      toolbarStyle.top = layer.y / imageScale + 60
                    }

                    const canDragByOuterEdge = isSelected(layer) && isDraggable(layer)

                    return (
                      <div className='absolute-full pointer-events-none z-2'>
                        { canDragByOuterEdge &&
                          <>
                            <div style={{ height: 10, top:    -5, left: 0, right: 0 }} className='absolute cursor-grab bg-blue opacity-0 pointer-events-all z-down' onPointerDown={ e => startDragging(e, layer)} />
                            <div style={{ height: 10, bottom: -5, left: 0, right: 0 }} className='absolute cursor-grab bg-blue opacity-0 pointer-events-all z-down' onPointerDown={ e => startDragging(e, layer)} />
                            <div style={{ width:  10, left:   -5, top: 0, bottom: 0 }} className='absolute cursor-grab bg-blue opacity-0 pointer-events-all z-down' onPointerDown={ e => startDragging(e, layer)} />
                            <div style={{ width:  10, right:  -5, top: 0, bottom: 0 }} className='absolute cursor-grab bg-blue opacity-0 pointer-events-all z-down' onPointerDown={ e => startDragging(e, layer)} />
                          </>
                        }


                        { canResize && <>
                          <div onPointerDown={e => startResize(e, layer, 'top')}           className={classNames('pointer-events-all cursor-ns-resize   z-1 bg-white hover_bg-mild-blue border border-333 absolute opacity-90 rounded-50 shadow-lg', {invisible: (resizing && resizing !== 'top'         ), 'bg-mild-blue': resizing === 'top'         })} style={{width: handleDiameter, height: handleDiameter, top:    -handleOffset, left: '50%', transform: 'translateX(-50%)'}}/>
                          <div onPointerDown={e => startResize(e, layer, 'bottom')}        className={classNames('pointer-events-all cursor-ns-resize   z-1 bg-white hover_bg-mild-blue border border-333 absolute opacity-90 rounded-50 shadow-lg', {invisible: (resizing && resizing !== 'bottom'      ), 'bg-mild-blue': resizing === 'bottom'      })} style={{width: handleDiameter, height: handleDiameter, bottom: -handleOffset, left: '50%', transform: 'translateX(-50%)'}}/>
                          <div onPointerDown={e => startResize(e, layer, 'left')}          className={classNames('pointer-events-all cursor-ew-resize   z-1 bg-white hover_bg-mild-blue border border-333 absolute opacity-90 rounded-50 shadow-lg', {invisible: (resizing && resizing !== 'left'        ), 'bg-mild-blue': resizing === 'left'        })} style={{width: handleDiameter, height: handleDiameter, left:   -handleOffset, top:  '50%', transform: 'translateY(-50%)'}}/>
                          <div onPointerDown={e => startResize(e, layer, 'right')}         className={classNames('pointer-events-all cursor-ew-resize   z-1 bg-white hover_bg-mild-blue border border-333 absolute opacity-90 rounded-50 shadow-lg', {invisible: (resizing && resizing !== 'right'       ), 'bg-mild-blue': resizing === 'right'       })} style={{width: handleDiameter, height: handleDiameter, right:  -handleOffset, top:  '50%', transform: 'translateY(-50%)'}}/>

                          <div onPointerDown={e => startResize(e, layer, 'top-left')}      className={classNames('pointer-events-all cursor-nwse-resize z-1 bg-white hover_bg-mild-blue border border-333 absolute opacity-90 rounded-50 shadow-lg', {invisible: (resizing && resizing !== 'top-left'    ), 'bg-mild-blue': resizing === 'top-left'    })} style={{width: handleDiameter, height: handleDiameter, top:    -handleOffset, left:  -handleOffset}}/>
                          <div onPointerDown={e => startResize(e, layer, 'top-right')}     className={classNames('pointer-events-all cursor-nesw-resize z-1 bg-white hover_bg-mild-blue border border-333 absolute opacity-90 rounded-50 shadow-lg', {invisible: (resizing && resizing !== 'top-right'   ), 'bg-mild-blue': resizing === 'top-right'   })} style={{width: handleDiameter, height: handleDiameter, top:    -handleOffset, right: -handleOffset}}/>
                          <div onPointerDown={e => startResize(e, layer, 'bottom-left')}   className={classNames('pointer-events-all cursor-nesw-resize z-1 bg-white hover_bg-mild-blue border border-333 absolute opacity-90 rounded-50 shadow-lg', {invisible: (resizing && resizing !== 'bottom-left' ), 'bg-mild-blue': resizing === 'bottom-left' })} style={{width: handleDiameter, height: handleDiameter, bottom: -handleOffset, left:  -handleOffset}}/>
                          <div onPointerDown={e => startResize(e, layer, 'bottom-right')}  className={classNames('pointer-events-all cursor-nwse-resize z-1 bg-white hover_bg-mild-blue border border-333 absolute opacity-90 rounded-50 shadow-lg', {invisible: (resizing && resizing !== 'bottom-right'), 'bg-mild-blue': resizing === 'bottom-right'})} style={{width: handleDiameter, height: handleDiameter, bottom: -handleOffset, right: -handleOffset}}/>
                        </>}

                        {showLayerTools && (
                          <div className='absolute z-3 top left right flex-row gap-2 flex-justify-center pointer-events-none' style={toolbarStyle}>
                            <div className={classNames('pointer-events-all border border-333 bg-white rounded relative shadow-lg opacity-70 transition hover_opacity-100', {hidden: !canDelete})}>
                              <button className='text-sm button px-2 py-1' disabled={!canDelete}
                                onPointerDown={e => {e.stopPropagation()}}
                                onPointerUp={e => {removeSelectedLayers(); e.stopPropagation()}}
                              >
                                <TrashIcon/>
                                <Tooltip>
                                  Delete
                                </Tooltip>
                              </button>
                            </div>
                            <div className={classNames('pointer-events-all border border-333 bg-white rounded relative shadow-lg opacity-70 transition hover_opacity-100')}>
                              <button className={classNames('text-sm button px-2 py-1 cursor-grab', {'cursor-grabbing': dragging})}
                                onPointerDown={e => {startDragging(e, layer); e.stopPropagation()}}
                                onPointerUp={e => {setDragging(false); e.stopPropagation()}}
                              >
                                <MoveIcon/>
                                <Tooltip>
                                  Drag to Move
                                </Tooltip>
                              </button>
                            </div>
                            <div className={classNames('pointer-events-all border border-333 bg-white rounded relative shadow-lg opacity-70 transition hover_opacity-100')}>
                              <button className='text-sm button px-2 py-1 pointer' disabled={!canDelete}
                                onPointerDown={e => {e.stopPropagation()}}
                                onPointerUp={e => {handleLayerRightClick(e); e.stopPropagation()}}
                              >
                                <EllipsisHorizontalIcon/>
                                <Tooltip>
                                  More Options
                                </Tooltip>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })()}

                  <div
                    className="layer-scaler relative absolute-full"
                    style={{
                      height: layer.height,
                      width:  layer.width ,
                      // height: 'auto',
                      transform: `scale(${1/imageScale})`,
                      transformOrigin: 'top left',
                    }}
                  >
                    {renderLayer(layer)}
                  </div>
                </div>
              )})}
            </div>
          </div>
        </div>


        <div id="tool-panel" className="flex-col ml-3 gap-3" style={{width: 300}}>

          <div id="top-bar" className="flex-row spread full-width">
            <div className="flex-row gap-1">
              <PopupMenu
                className="px-2 default-text-color flex-center"
                tooltip="Add Something..."
                icon={<PlusIcon/>}
                closeOnClick
              >
                <button className='popup-item flex-row gap-1' style={{height: 40}} onClick={addImageLayer} disabled={!canAddImageLayer}>
                  <PhotoIcon/>
                  Add Image
                </button>
                <button className='popup-item flex-row gap-2' style={{height: 40}} onClick={addLogoLayer} disabled={!canAddLogoLayer}>
                  <CubeIcon/>
                  Add Logo
                  { !canAddLogoLayer && <Tooltip>Only 1 Logo Allowed</Tooltip>}
                </button>
                <button className='popup-item flex-row gap-2' style={{height: 40}} onClick={addTextBoxLayer} disabled={!canAddTextBoxLayer}>
                  <TextIcon/>
                  Add Text
                </button>
                <hr className='popup-divider'/>
                <button className='popup-item flex-row gap-2' style={{height: 40}} onClick={() => addShapeLayer('circle')} disabled={!canAddImageLayer}>
                  <CircleShapeIcon filled/>
                  Add Circle
                </button>
                <button className='popup-item flex-row gap-2' style={{height: 40}} onClick={() => addShapeLayer('rectangle')} disabled={!canAddImageLayer}>
                  <SquareShapeIcon filled/>
                  Add Rectangle
                </button>
              </PopupMenu>

              <button className='button px-2' style={{height: 40}} onClick={addTextBoxLayer} disabled={!canAddTextBoxLayer}>
                <TextIcon/>
                <Tooltip>Add Text</Tooltip>
              </button>
              <button className='button px-2' style={{height: 40}} onClick={addImageLayer} disabled={!canAddImageLayer}>
                <PhotoIcon/>
                <Tooltip>Add Image</Tooltip>
              </button>
            </div>

            <div className='flex-row gap-1'>
              <button className='button px-2' style={{height: 40}} onClick={() => setLayersVisible(v => !v)}>
                <Square3Stack3dIcon/>
                <Tooltip>
                  {layersVisible ? 'Hide' : 'Show'} Layer List
                </Tooltip>
              </button>
            </div>

          </div>

          <div id="image-editor-tools" className='flex-grow rounded overflow-auto-y overflow-hidden-x relative'>
            <div className='absolute-full z-up shadow-inset-lg pointer-events-none'></div>
            <div className='absolute-full' onClick={() => deselectAllLayers()}></div>

            <div className="flex-row flex-stretch full-height transition-slow" style={{transform: advancedToolVisible ? `translateX(-300px)` : 'translateX(0px)'}}>

              <div className='tool-area flex-col bg-f8f8f8 py-5 px-4 gap-6 full-height flex-align-start' style={{width: 300}} onClick={deselectAllOnDirectClick}>

                <div className={classNames('relative full-width', {'disabled-ui-group': !canChangeColor})}>
                  <ColorPicker
                    layers={layers}
                    brandColors={brandColors}
                    selectedLayerIds={activeLayerIds}
                    onChange={newColor => changeSelected({color: newColor})}
                  />
                </div>

                <div className={ classNames('flex-row relative full-width', { 'disabled-ui-group': !canChangeOpacity })} >
                  <div className='flex-row gap-3 pr-2 full-width'>

                    <div className='flex-col flex-align-center full-width'>
                      <label className='flex-row flex-justify-end gap-1'>
                        Shadow
                        <button className='button-link text-xs p-0' onClick={() => {if (!selectedLayersShadow) handleToggleShadow(); showAdvancedTools('shadow')}} style={{lineHeight: '0.5rem'}}>
                          <EditIcon/>
                          <Tooltip>
                            Edit Shadow
                          </Tooltip>
                        </button>
                      </label>
                      <input
                        className="full-width"
                        type="range"
                        value={selectedLayersShadow.opacity}
                        min="0"
                        max="100"
                        onChange={e => changeSelected({shadow: {...(selectedLayersShadow), opacity: Number(e.target.value) }})}
                      />
                    </div>

                    <div className='flex-col flex-align-center full-width'>
                      <label>Transparency</label>
                      <input
                        className="full-width"
                        type="range"
                        value={selectedLayersOpacity}
                        min="0"
                        max="1"
                        step={0.01}
                        onChange={e => changeSelected({opacity: Number(e.target.value)})}
                      />
                    </div>

                  </div>
                </div>

                <div className='flex-col gap-3 full-width'>

                  <div className='flex-row spread gap-3 full-width'>

                    <div className={classNames('flex-row full-width', {'disabled-ui-group': !canChangeAlignment})}>
                      <button
                        className={classNames('button px-2 flex-row gap-1 flex-justify-start', {'is-primary': selectedLayersAlignment.x === 'left'})}
                        onClick={() => changeSelected({alignX: 'left'})}
                      >
                        <CustomAlignmentIcon value="left" />
                        <span className={classNames({hidden: showVerticalAlignment})}>
                          Left
                        </span>
                      </button>
                      <button
                        className={classNames('button px-2 flex-row gap-1 flex-justify-center', {'is-primary': selectedLayersAlignment.x === 'center'})}
                        onClick={() => changeSelected({alignX: 'center'})}
                      >
                        <CustomAlignmentIcon value="center" />
                        <span className={classNames("pr-3", {hidden: showVerticalAlignment})}>
                          Center
                        </span>
                      </button>
                      <button
                        className={classNames('button px-2 flex-row gap-1 flex-justify-end', {'is-primary': selectedLayersAlignment.x === 'right'})}
                        onClick={() => changeSelected({alignX: 'right'})}
                      >
                        <span className={classNames({hidden: showVerticalAlignment})}>
                          Right
                        </span>
                        <CustomAlignmentIcon value="right" />
                      </button>
                    </div>

                    <div className={classNames('flex-row full-width', {hidden: !showVerticalAlignment, 'disabled-ui-group': !canChangeAlignment})}>
                      <button
                        className={classNames('button px-2 flex-row gap-1 flex-justify-start', {'is-primary': selectedLayersAlignment.y === 'top'})}
                        onClick={() => changeSelected({alignY: 'top'})}
                      >
                        <CustomAlignmentIcon value="top" />
                      </button>
                      <button
                        className={classNames('button px-2 flex-row gap-1 flex-justify-center', {'is-primary': selectedLayersAlignment.y === 'middle'})}
                        onClick={() => changeSelected({alignY: 'middle'})}
                      >
                        <CustomAlignmentIcon value="middle" />
                      </button>
                      <button
                        className={classNames('button px-2 flex-row gap-1 flex-justify-end', {'is-primary': selectedLayersAlignment.y === 'bottom'})}
                        onClick={() => changeSelected({alignY: 'bottom'})}
                      >
                        <CustomAlignmentIcon value="bottom" />
                      </button>
                    </div>

                  </div>

                  <div className='flex-row full-width'>
                    <div className={classNames('flex-row full-width', {'disabled-ui-group': !textboxesSelected})}>
                      <button className='button full-width flex-row flex-justify-start' onClick={() => showAdvancedTools('font')}>
                        {selectedLayersFont ?
                          <div className='flex-row flex-align-center flex-justify-start gap-1 nowrap overflow-hidden'>
                            <span className=''>
                              Font:
                            </span>
                            <span className="" style={{fontFamily: selectedLayersFontId}}>
                              {selectedLayersFont.family} {selectedLayersFont.name.endsWith('Italic') ? 'Italic' : ''}
                            </span>
                            <span className='gray '>
                              ({selectedLayersFont.name.split('-')[1]?.replace('Italic', '') || 'Regular'})
                            </span>
                          </div>
                        :
                          <span className=''>
                            Font
                          </span>
                        }
                      </button>
                    </div>
                  </div>

                </div>
              </div>

              <div id="advanced-tools" className="bg-f8f8f8 p-2 full-width flex-no-shrink flex-col" style={{width: 300}}>
                <div className='full-width'>
                  <div className='flex-row spread gap-2 p-2'>
                    <div style={{width: 40}}>
                      <button className='button icon-button icon-20' onClick={hideAdvancedTools}>
                        <ArrowSmallLeftIcon/>
                        <Tooltip>
                          Back
                        </Tooltip>
                      </button>
                    </div>
                    <div className='flex-grow text-lg bold text-center'>
                      Adjust {titleize(advancedToolName)}
                    </div>
                    <div style={{width: 40}}>
                    </div>
                  </div>
                </div>

                <div className={classNames({hidden: advancedToolName !== 'shadow'})}>
                  <ShadowFineTuning
                    shadow={selectedLayersShadow}
                    colorEditable={canEditShadowColor}
                    onChange={shadow => changeSelected({shadow})}
                  />
                </div>

                <div className={classNames('flex p-2 relative overflow-hidden', {hidden: advancedToolName !== 'font'})}>
                  <FontUI
                    fonts={fonts}
                    uid={selectedLayersFontId}
                    size={selectedLayersFontSize}
                    kerning={selectedLayersKerning}
                    spacing={selectedLayersLineSpacing}
                    onChange={data => changeSelected(data, {historySquashKey: 'font'})}
                    visible={advancedToolName === 'font' && advancedToolVisible}
                  />
                </div>

              </div>
            </div>
          </div>


          <div className='bottom-bar flex flex-row spread gap-2 relative'>

            <div id="undo-and-redo" className='flex-row z-1'>
              <div className='relative'>
                <button className='button icon-20 px-2' disabled={!history.canUndo} onClick={history.undo}>
                  <ArrowUturnLeftIcon/>
                  <Tooltip>
                    Undo
                  </Tooltip>
                </button>
              </div>

              <div className='relative'>
                <button className='button icon-20 px-2' disabled={!history.canRedo} onClick={history.redo}>
                  <ArrowUturnRightIcon/>
                  <Tooltip>
                    Redo
                  </Tooltip>
                </button>
              </div>
            </div>

            <div id="history-info" className='flex-col gap-2 hidden'>
              <div className='absolute text-xs' style={{marginTop: -21, marginLeft: -20}}>
                {history.squashKey}
              </div>
              <div className='flex-row gap-2'>
              {history.index} of {history.length}
              </div>
            </div>

            <div className='flex-row gap-1'>
              { hasEdits ?
                <>
                  <button  onClick={revert} className='button'>
                    Close
                  </button>

                  <button className='button is-primary' onClick={save} disabled={!canSave}>
                    Save
                    { !canSave &&
                      <Tooltip>
                        Cannot save an image<br/>with empty TextBox layers
                      </Tooltip>
                    }
                  </button>
                </>
              :
                <button  onClick={revert} className='button is-primary'>
                  Close
                </button>
              }
            </div>
          </div>
        </div>


        <div className={classNames('layer-panel flex-col gap-3 overflow-hidden transition-slow', {'ml-3': layersVisible})} style={{maxWidth: layersVisible ? 250 : 0, opacity: layersVisible ? 1 : 0}}>
          <div className='flex-row spread flex-align-center'>
            <h2 className='flex-row gap-1 m-0 py-1 pl-1' style={{height: 40}}>
              <span className="gray text-lg">
                Layers
              </span>
              <span className="not-bold gray text-base">
                ({layers.length})
              </span>
            </h2>

            <PopupMenu small inverted closeOnClick icon={<CogSixToothIcon/>} tooltip="Settings">
              <div id='canvas-tools' className='flex-col gap-4 px-3 py-2 text-base max-width-280'>
                <div className='flex-row gap-2 spread'>
                  <span className="gray bold text-lg">
                    Image Editor Settings
                  </span>
                  <CopyToClipboard text={image.id.toString()}>
                    <div className='text-sm opacity-0 transition hover_opacity-100 cursor-pointer mt-1'>
                      <CopyIcon/>
                    </div>
                  </CopyToClipboard>
                </div>

                <div className='text-sm gray'>
                  These settings will NOT affect the final image. They just make it easier to work with the layers.
                </div>

                <div className='flex-col'>
                  <div className='flex-row spread'>
                    <a className='z-1 center dark-gray'>
                      <label>
                        Canvas Brightness
                      </label>
                      <Tooltip>
                        This changes the canvas background to to make it easier to see gray layers.
                      </Tooltip>
                    </a>
                  </div>
                  <input
                    className=''
                    type="range"
                    value={canvasBrightness}
                    min="0"
                    max="1"
                    step="0.001"
                    onChange={e => setCanvasBrightness(e.target.value)}
                  />
                </div>

                <div className='flex-col'>
                  <a className='dark-gray'>
                    <label>
                      Selected Layer Focus
                    </label>
                    <Tooltip>
                      When you have a layer selected, this lets you change the opacity of the other layers, which is useful when repositioning.
                    </Tooltip>
                  </a>
                  <input
                    className=''
                    type="range"
                    value={selectionFocus}
                    min="0"
                    max="1"
                    step="0.01"
                    onChange={e => setSelecionFocus(e.target.value)}
                  />
                </div>

                <div className={classNames('relative flex-row gap-2', {hidden: !canChangeBrandable})} style={{marginLeft: -8}}>
                  <Toggle checked={previewBranding} onChange={() => setPreviewBranding(v => !v)} >
                  </Toggle>
                  <div>
                    Preview Branding
                    <Tooltip>
                      Check this box to get a (rough) idea of what might happen when this image gets branded. The will NOT have any effect on the final image.
                    </Tooltip>
                  </div>
                </div>

                <button className='button is-inverted' onClick={toggleShortcuts}>
                  Show Keyboard Shortcuts
                </button>

                { canBeTemplate && <>
                  <button className='button is-inverted' onClick={closeMenuAnd(createTemplate)} disabled={hasEdits}>
                    Copy to New Image Template
                  </button>
                </>}

              </div>

            </PopupMenu>

          </div>

          <div className={classNames("layers-list flex-grow flex-col gap-2 px-1 py-3", {'layer-drag-in-progress': !!layerInDrag})} style={{height: image.height}}>

            {reversedLayers.map(layer => {
              const active = activeLayerIds.includes(layer.id)
              const inactive = activeLayerIds.length && !active

              function toggleBrandable(e) {
                toggleLayerBrandable(layer)
              }

              function toggle (e) {
                const maintainSelection = (e.controlKey || e.metaKey || e.shiftKey)

                let selectedIds = maintainSelection ? activeLayerIds : []

                if (e.shiftKey && selectedIds.length> 0 ) {
                  const zRange = sort([ layer.z, layers.find(l => l.id === last(activeLayerIds)).z ])
                  selectedIds = [
                    ...selectedIds,
                    ...layers.filter(l => (
                      !selectedIds.includes(l.id) &&
                      l.z >= first(zRange) &&
                      l.z <= last(zRange)
                    )).map(l => l.id),
                  ]
                } else if (activeLayerIds.length === 1 && activeLayerIds[0] === layer.id) {
                  selectedIds = []
                } else if (selectedIds.includes(layer.id)) {
                  selectedIds = selectedIds.filter(id => id !== layer.id)
                } else {
                  selectedIds = [...selectedIds, layer.id]
                }

                setActiveLayerIds(selectedIds)
              }

              function dragStart(e) {
                e.dataTransfer.effectAllowed = 'move'

                layerDragData.current.layer = layer
                setLayerDragZ(layer.z)
              }

              function dragEnter(e) {
                setLayerDragZ(layer.z)
              }

              function dragEnd(e) {
                const newZ = layerDragZ + (layerDragZ > layer.z ? 0.001 : -0.001)
                setLayerDragZ(-1)

                layerDragData.current = {}

                // Apply the new (non-integer) Z value to the layer that was moved
                const newLayers = layers.map(l => l.id === layer.id ? {...l, z: newZ} : l)

                // Reset the z values on the layers to use integers (while maintaining order)
                setLayers( zValueCleanup(newLayers) )
              }

              const dragging  = layerInDrag && layerInDrag.id === layer.id
              const slideUp   = layerInDrag && !dragging && layerDragZ >= 0 && layerDragZ <= layer.z && layer.z < layerInDrag.z
              const slideDown = layerInDrag && !dragging && layerDragZ >= 0 && layerDragZ >= layer.z && layer.z > layerInDrag.z

              const classes = classNames('layer-widget px-2', {active, inactive, brandable: layer.brandable, dragging, 'slide-up': slideUp, 'slide-down': slideDown})

              return (
                <div key={layer.id} className={classes} draggable="true" onDragStart={dragStart} onDragEnter={dragEnter} onDragEnd={dragEnd}>
                  <div className="layer-thumb">
                    <div className='img-wrapper' onClick={toggle}
                      style={{
                        width:  image.width  / layerScale,
                        height: image.height / layerScale,
                      }}
                    >
                      <div className="canvas-background absolute-full"/>

                      <div
                        className='absolute-full top left'
                        style={{
                          transform: `scale(${1/layerScale})`,
                          transformOrigin: 'top left',
                        }}
                      >

                      <div
                        className='absolute-full top left'
                        style={{
                          width: layer.width,
                          height: layer.height,
                          left: layer.x,
                          top:  layer.y,
                        }}
                      >

                        {renderLayer(layer, true)}
                      </div>
                      </div>
                    </div>
                  </div>
                  <div className="layer-controls p-1 flex-col spread flex-align-center text-sm bg-f8f8f8 rounded full-width">
                    <div className='flex'>
                      <button className='button dark-gray px-2 py-1 bg-none no-border is-inverted' onClick={() => removeLayersByid([layer.id])}>
                        <CloseIcon/>
                        <Tooltip>
                          Delete
                        </Tooltip>
                      </button>
                    </div>
                    { canChangeBrandable &&
                      <div className='flex'>
                        <button className={classNames('button px-2 py-1 no-border', {'is-primary': layer.brandable, 'is-inverted bg-none': !layer.brandable})} onClick={() => toggleBrandable(layer)}>
                          <SwatchIcon/>
                          <Tooltip>
                            Brandable
                          </Tooltip>
                        </button>
                      </div>
                    }
                    <div className='flex-row flex-align-center light-gray' style={{height: 40}}>
                      { layer.type === 'logo' ?
                        <span>
                          <CubeIcon/>
                          <Tooltip>Logo</Tooltip>
                        </span>
                      : layer.type === 'shape' ?
                        <span>
                          <StarIcon/>
                          <Tooltip>Shape</Tooltip>
                        </span>
                      : layer.type === 'textbox' ?
                        <span>
                          <span className='light-gray bold border border border-gray border-width-1 rounded' style={{fontFamily: 'serif', padding: '0px 4px', fontSize: '1.1rem'}}>T</span>
                          <Tooltip>Text</Tooltip>
                        </span>
                      : isGroup(layer) ?
                        <span>
                          <FolderIcon/>
                          <Tooltip>Group</Tooltip>
                        </span>
                      : layer.type === 'video' ?
                        <span>
                          <VideoCameraIcon/>
                          <Tooltip>Video</Tooltip>
                        </span>
                      :
                        <span>
                          <PhotoIcon/>
                          <Tooltip>Image</Tooltip>
                        </span>
                      }
                    </div>
                  </div>
                </div>
              )
            })}

          </div>
        </div>

        { colorResetPosition && <>
          <div className='fixed z-1' style={{
            top:  colorResetPosition.y + (safari ? 30 : 45),
            left: colorResetPosition.x - (safari ? 90 : 10),
          }}>
            <Popup visible >
              <div className='p-4 bg-white rounded text-sm dark-gray flex-col gap-3' style={{maxWidth:250}}>
                <p>
                  If you pick a color for the logo layer, and the user has a logo
                  with the "Colorized" style, their logo will be recolored to
                  match the selected color.
                </p>
                <p>
                  <button className='link no-border p-0 bg-none text-sm' style={{color: 'red'}} onClick={handleRemoveLogoColor}>
                  Remove the color from this logo</button> if&nbsp;a&nbsp;semi-transparent logo
                  does NOT look good for this image.
                </p>
              </div>
            </Popup>
          </div>
        </>}

        <Modal visible={showUpload} onClickOutside={() => setShowUpload(false)}>
          <div className='padded content center-text' style={{width: '80vw', maxWidth: 400}}>
            <h2>Upload an Image</h2>
            <FileUploader onUpload={handleUpload} multiple/>
            <div className="button-container" style={{marginTop: '1rem'}}>
              <button className="button" onClick={() => setShowUpload(false)}>
                Cancel
              </button>
            </div>
          </div>
        </Modal>

        <Modal visible={showGalleryPicker} onClickOutside={() => setShowGalleryPicker(false)}>
          <div className="flex-col" style={{width: '80vw', maxWidth: 500, maxHeight: '80vh'}}>
            <div className="flex-row spread p-3 border-bottom">
              <h2 className="text-lg bold m-0">Add Image</h2>
              <button className="button no-border is-inverted" onClick={() => setShowGalleryPicker(false)}>
                <CloseIcon/>
              </button>
            </div>

            <div className="p-3 flex-row gap-2">
              <button className="button gap-1" onClick={() => { setShowGalleryPicker(false); setShowUpload(true) }}>
                <PhotoIcon/> Upload
              </button>
            </div>

            <div className="px-3">
              <input
                className="input full-width"
                type="text"
                placeholder="Search your images..."
                value={galleryQuery}
                onChange={e => handleGallerySearchInput(e.target.value)}
              />
            </div>

            <div className="flex-grow overflow-auto-y p-3">
              { galleryAssets.length === 0 ?
                <p className="gray text-center">
                  { galleryQuery ? 'No images found.' : 'No images in your gallery yet.' }
                </p>
              :
                <div className="grid grid-2 sm_grid-3 gap-3">
                  { galleryAssets.map(asset => (
                    <div
                      key={asset.id}
                      className="rounded-lg overflow-hidden shadow-lg pointer hover_scale transition bg-ccc"
                      onClick={() => pickFromGallery(asset)}
                    >
                      <img
                        src={asset.thumbnailUrl || asset.url}
                        alt={asset.description || ''}
                        className="full-width"
                        style={{height: 'auto', display: 'block'}}
                      />
                    </div>
                  ))}
                </div>
              }
            </div>
          </div>
        </Modal>


        <Modal visible={shortcutsVisible} onClickOutside={toggleShortcuts}>
          <div className='pt-4 pb-5 px-5'>
            <div className='flex-row spread' style={{marginRight: '-1rem'}}>
              <h2 className='text-xl'>
                Keyboard Shortcuts
              </h2>
              <button className='button no-border is-inverted' onClick={toggleShortcuts}>
                <CloseIcon/>
              </button>
            </div>
            <div className='shortcut-list border mt-4 bg-eee p-4'>
              <span><b>Multi-Select Layers:</b></span>
              <span>Shift + Click </span>

              <span><b>Toggle Layer Selection:</b></span>
              <span>{ctrlKeyName} + Click </span>

              <span><b>Deselect All Layers:</b></span>
              <span>{ctrlKeyName} + D</span>

              <span><b>Finish Editing Text:</b></span>
              <span>Esc <span className="gray">or</span> {ctrlKeyName} + Enter</span>

              <span><b>Delete Selected Layers:</b></span>
              <span>Backspace <span className="gray">or</span> Delete</span>

              <span><b>Undo Last Change:</b></span>
              <span>{ctrlKeyName} + Z </span>

              <span><b>Redo (after undo):</b></span>
              <span>{ctrlKeyName} + Shift + Z </span>

              <span><b>Group Selected Layers:</b></span>
              <span>{ctrlKeyName} + G </span>

              <span><b>Ungroup Selected Layer:</b></span>
              <span>{ctrlKeyName} + Shift + G </span>

              <div className='bg-ddd' style={{height: 1}}></div>
              <div className='bg-ddd' style={{height: 1}}></div>

              <span><b>Prevent Snapping:</b></span>
              <span>{ctrlKeyName} <span className='gray'>(while moving layer)</span> </span>

              <span><b>Symetrical Resize:</b></span>
              <span>Alt <span className='gray'>(while resizing layer)</span> </span>
            </div>
          </div>
        </Modal>

        { contentMenuPosition && <>
          <div className='fixed' style={{top: contentMenuPosition.y, left: contentMenuPosition.x}}>
            <Popup visible onClickOutside={() => setContentMenuPosition(null)}>
              <div className='p-2 bg-white rounded'>
                { contextMenuType === 'layer' ?
                  layerContextMenu
                :
                  contextMenu
                }
              </div>
            </Popup>
          </div>
        </>}
      </div>
    </FileUploader>

    <style jsx>{`
      ${fonts.map(font => {
        if (font.url) {
          return `@font-face { font-family: '${font.uid}'; src: url('${font.url}') format('truetype'); }`
        }
      }).filter(Boolean).join('\n')}
    `}</style>

    <style jsx>{`
      :global(.button.icon-20 svg) {
        height: 20px !important;
        max-height: 20px !important;
        min-width: 20px !important;
      }
      :global(.button.icon-18 svg) {
        height: 18px !important;
        max-height: 18px !important;
        min-width: 18px !important;
      }
      .disabled-ui-group {
        opacity: 0.4;
        pointer-events: none;
        filter: grayscale(1);
      }
      .button.color-button {
        box-shadow: 0 0 8px rgba(0, 0, 255, 0.5);
      }
      .button.color-button:hover {
        filter: none;
        z-index: 1;
        transform: scale(1.1);
        box-shadow: 0 0 12px rgba(0, 0, 255, 0.8);
        transition: all 200ms;
      }
      .button[disabled] {
        opacity: 0.3;
      }
      .shortcut-list {
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 0.5rem 1rem;
        width: fit-content;
        color: #333;
        white-space: nowrap;
        z-index: 99;
      }
      .shortcut-list > span:nth-child(2n) {
        text-align: left;
      }
      .action-menu-button {
        padding: 0;
        margin: 0;
        border: none;
        background: none;
        display: flex;
      }
      .popup {
        position: absolute;
        right: 2rem;
        top: -0.5rem;
        display: none;
        background-color: white;
        padding: 1.5rem;
        border-radius: 8px;
        width: fit-content;
        box-shadow: 0 0 8px rgba(0 0 0 / 0.5);
        color: #333;
        z-index: 99;
      }
      .image-preview {
        position: relative;
        flex: auto;
      }
      .color-picker {
        width: 28px;
      }
      .disabled .color-picker {
        opacity: 0.2;
      }
      .layer-color {
        height: 20px;
        width: 20px;
        border-radius: 100%;
        display: inline-block;
        border: 1px solid #ccc;
        box-shadow: inset 0 1px 2px rgb(0 0 0 / 33%);
        position: relative;
      }
      .layer-color.no-color:after {
        content: '?';
        font-size: 0.9rem;
        color: red;
        position: absolute;
        margin-left: 0.4rem;
      }
      .layer-panel {
        max-height: 100%;
      }
      .layers-list {
        max-height: 100%;
        min-width: 220px;
        overflow-y: scroll;
        overflow-x: hidden;
        box-shadow: inset 0 0 8px rgba(0, 0, 0, 0.3);
        background-color: white;
        border-radius: 8px;
        border: 1px solid #ddd;
      }
      .layer-widget {
        display: flex;
        align-items: stretch;
      }
      .layer-drag-in-progress .layer-widget {
        transition: all 200ms;
      }
      .layer-widget.dragging {
        opacity: 0;
      }
      .layer-widget.slide-up {
        transform: translateY(-100%)
      }
      .layer-widget.slide-down {
        transform: translateY(100%)
      }
      .layer-controls {
        border: 1px solid #ddd;
      }
      .delete-layer {
        /* position: absolute;
        right: -4px;
        top: -4px; */
      }
      .layer-thumb:hover {
        outline: 4px solid rgba(255, 89, 0, 0.2);
        border-radius: 4px;
        z-index: 1;
      }
      .delete-layer {
        padding: 0.25rem 0.7rem;
        border: 1px solid rgba(255, 0, 0, 0.5);
      }
      .delete-layer:hover {
        background-color: red;
      }
      .delete-layer:hover :global(.icon) {
        color: white;
      }
      .layer {
        position: absolute;
        display: grid;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
      }
      .center-line:before,
      .center-line {
        border-left: 1px solid rgb(231, 0, 0);
        left: calc(50% - 1px);
        position: absolute;
        content: '';
        top: 0;
        bottom: 0;
        z-index: 1;
      }
      .center-line:before {
        border-left: 3px solid rgba(255, 255, 255, 0.4);
        left: calc(50% - 2px);
      }
      .middle-line:before,
      .middle-line {
        border-top: 1px solid rgb(231, 0, 0);
        top: calc(50% - 1px);
        position: absolute;
        content: '';
        left: 0;
        right: 0;
        z-index: 1;
      }
      .middle-line:before {
        border-top: 3px solid rgba(255, 255, 255, 0.4);
        top: calc(50% - 2px);
      }
      .layer.draggable {
        cursor: move;
        cursor: grab;
      }
      .drag-in-progress .layer.draggable {
        cursor: grabbing !important;
      }
      .layer .layer-contents {
        user-select: none;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
      }
      .thumbnail-image {
        position: relative;
        user-select: none;
      }
      .layer:first-child {
        position: relative;
      }
      .image-preview .layer.inactive {
        opacity: ${inactiveLayerOpacity};
      }
      .layer-thumb {
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: center;
        background-color: #ccc;
      }
      .layer-thumb .logo-area,
      .layer-thumb .img-wrapper .layer-contents {
        display: block;
        position: relative;
      }
      .layer-thumb .img-wrapper {
        cursor: pointer;
        line-height: 0;
        overflow: hidden;
      }
      .image-preview,
      .layer-thumb .img-wrapper {
        position: relative;
        outline: 1px solid #ddd;
      }
      .image-preview .canvas-background,
      .layer-thumb .img-wrapper .canvas-background {
        content: "";
        background-image: url('/img/grid.png');
        background-repeat: repeat;
        background-size: 16px auto;
        position: absolute;
        top: 0px;
        right: 0px;
        bottom: 1px;
        left: 0px;
        filter: brightness(${parseFloat(canvasBrightness) + 0.15});
        outline: 1px solid #ddd;
      }
      .layer-thumb .img-wrapper .canvas-background {
        background-size: 8px auto;
      }
      .layer-widget.active .layer-thumb {
        z-index: 1;
      }
      .layer-widget.inactive .img-wrapper {
        opacity: 0.3;
      }
      .layer-widget.inactive:hover {
        opacity: 0.8;
      }
      .layer-widget.active .img-wrapper {
        outline: 3px dashed rgba(255, 89, 0, 0.7);
        outline-offset: 2px;
        border-radius: 4px;
      }
      .layer-thumb:last-child .layer-contents {
        margin-bottom: 0;
      }
      .locked {
        pointer-events: none;
      }
      .selectable {
        cursor: pointer;
      }
      .selectable:hover::before,
      .selectable:hover::after,
      .layer.selected::before,
      .layer.selected::after {
        content: "";
        position: absolute;
        outline-width: 2px;
        outline-style: dashed;
        outline-color: #000000AA;
        outline-offset: -1px;
        top:     0;
        left:    0;
        right:   0;
        bottom:  0;
        z-index: 1;
        pointer-events: none;
      }
      .selectable:hover::before,
      .layer.selected::before {
        outline-color: #FFFFFFDD;
        outline-style: solid;
      }
      .selectable:hover::before {
        outline-color: #00000033;
      }
      .selectable:hover::after {
        outline-color: #FFFFFF66;
      }

    `}</style>
  </>
}

// Grouped layers may have non-consecuitive z values, which is not pretty.
// Recently ungrouped layers will have z values with decimals (to keep them
// together in the z-stack when merged with the other root-level layers). This
// function will convert the z values to consecutive integers, while maintaining
// the correct z order of the layers.
function zValueCleanup(layerList) {
  return sortBy(layerList, 'z').map((l, i) => ({...l, z: i}))
}


function cleanupText (text) {
  if (typeof text !== 'string') return text

  // Remove any em-dashes and en-dashes
  text = text.replace(/[\u2014]/g, ', ') // Em-dash becomes a comma
  text = text.replace(/[\u2013]/g, '-')  // En-dash becomes a hyphen

  // Remove emoji (and other emoji-like) characters (we can't render them)
  text = text.replace(/[\p{Extended_Pictographic}]/ug, '')

  // Collapse consecutive spaces
  text = text.replace(/ +/g, ' ')

  // Remove leading and trailing spaces
  text = text.trim()

  return text
}


function hexToRgba(hex, opacity) {
  hex = hex.replace(/^#/, '')

  let r, g, b

  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16)
    g = parseInt(hex[1] + hex[1], 16)
    b = parseInt(hex[2] + hex[2], 16)
  } else if (hex.length === 6) {
    r = parseInt(hex.slice(0, 2), 16)
    g = parseInt(hex.slice(2, 4), 16)
    b = parseInt(hex.slice(4, 6), 16)
  }

  const alpha = opacity / 100
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}


// This is basically a 90 degree shift in hue function, but we override the
// luminosity and saturation to force a more color rich (and bright) color
function generateBrandingColor(hex) {
  // Rotate the hue to get something different than the original layer color
  const degrees = 90

  // Remove # if present
  hex = hex.replace(/^#/, '');

  // Convert hex to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  // Convert RGB to HSL
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  // Override luminosity and saturation for a strong (not too dark) color
  l = 0.7
  s = 1

  // Rotate hue
  h = (h * 360 + degrees) % 360;
  if (h < 0) h += 360;
  h = h / 360;

  // Convert HSL back to RGB
  let r2, g2, b2;
  if (s === 0) {
    r2 = g2 = b2 = l;
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r2 = hue2rgb(p, q, h + 1/3);
    g2 = hue2rgb(p, q, h);
    b2 = hue2rgb(p, q, h - 1/3);
  }

  // Convert RGB back to hex
  const toHex = x => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return '#' + toHex(r2) + toHex(g2) + toHex(b2);
}


async function copyToClipboard(text) {
  if (!navigator.clipboard) {
    alert('Your browser does not support copy/paste.')
    return
  }

  try {
    await navigator.clipboard.writeText(text)
  } catch (err) {
    console.warn('Failed to copy to clipboard with new API')
    console.error(err)
  }
}


function newLayerId() {
  return `new-${new Date().valueOf()}_${Math.random()}`
}